import { useEffect, useState } from 'react';
import { Button, Checkbox, Flex, Form, Input, Typography } from 'antd';
import type { FormProps } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';
import { isRTL } from 'core/services/language';
import { checkPhoneValidation } from 'utils/validators/phone';

// components
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import LanguageButton from 'components/language-button/language-button';
import PageTitle from 'components/title/title';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import FormPassword from 'components/formPassword/formPassword';

// styles
import styles from './login.module.scss';
import darkLogo from 'assets/images/logo-dark.svg';
import lightLogo from 'assets/images/logo-light.svg';
import playStoreIcon from 'assets/icons/playStore.webp';
import appStoreIcon from 'assets/icons/appStore.webp';
import { onGetTanaqulUrl, onLogin } from '../services/loginService';
import { LoginPayload, TanaqulUrlResponse } from '../models/login';
import authService from 'core/services/auth';
import { useDispatch, useSelector } from 'react-redux';
import { setAdmin } from 'stores/useAdminStore';
import { setCompany } from 'stores/useCompanyStore';
import { RootState } from 'stores/store';

function Animate({ children, delay }: { children: any; delay: number }) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{
				duration: 0.2,
				delay,
			}}>
			{children}
		</motion.div>
	);
}

function LoginPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const [loginForm] = Form.useForm();
	const [isLoading, setIsLoading] = useState(false);
	const [remember, setRemember] = useState(false);
	const [registerModalOpen, setRegisterModalOpen] = useState(false);
	const [tanaqulAppsUrl, setTanaqulAppsUrl] = useState<TanaqulUrlResponse>();

	type FieldType = LoginPayload;

	useEffect(() => {
		getTanaqulAppsUrl();
	}, []);

	const getTanaqulAppsUrl = async () => {
		try {
			const response = await onGetTanaqulUrl();
			setTanaqulAppsUrl(response);
		} catch (error) {
			throw error;
		}
	};

	const onChangePhone = ({ number, dialCode }: PhoneInputReturn) => {
		loginForm.setFieldsValue({
			phone_number: number,
			phone_country_code: dialCode,
		});
	};

	const validatePhone = (_: any, value: any) => {
		if (checkPhoneValidation(loginForm.getFieldValue('phone_country_code'), value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		else return Promise.reject();
	};

	const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsLoading(true);
			const response = await onLogin(values);

			authService.setToken(response?.token, remember);
			dispatch(setAdmin(response.company_user));
			dispatch(setCompany(response.company_user.company));
			authService.setData(response?.is_main_user, response.permissions, response?.registration_step);

			switch (response.registration_step) {
				case 'contact_person':
					navigate('/register/otp');
					break;
				case 'phone_verified':
					navigate('/register/company');
					break;
				case 'company_profile':
					navigate('/register/plans');
					break;
				case 'plan_select':
					navigate('/register/agreement');
					break;
				default:
					navigate('/dashboard');
			}
		} catch (error: any) {
			setIsLoading(false);
			const message = error?.response?.data?.message || t('COMMON.INVALID_CREDENTIALS');
			if (error.status === 401) NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.LOGIN')} />

			<Flex vertical style={{ minHeight: '100vh', width: '100vw' }} justify="center" gap={40}>
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{
						duration: 0.5,
						delay: 1.2,
					}}>
					<Flex align="center" justify="flex-end" gap={20} style={{ position: 'absolute', top: 50, insetInlineEnd: 50 }}>
						<LanguageButton />
					</Flex>
				</motion.div>

				<Flex justify="space-evenly" align="center">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{
							duration: 0.5,
						}}
						className={`${styles.login_intro} mx-3`}>
						<Animate delay={0.2}>
							<div className={styles.login_intro_img}>
								<img src={lightLogo} alt="logo" />
							</div>
						</Animate>

						<div className={styles.login_intro_text}>
							<Animate delay={0.5}>
								<h1>{t('LOGIN.INTRO_TITLE')}</h1>
							</Animate>
							<Animate delay={0.8}>
								<p>{t('LOGIN.INTRO_SUBTITLE')}</p>
							</Animate>
						</div>
					</motion.div>

					<motion.div
						initial={window.outerWidth > 768 && { opacity: 0, x: isRTL() ? 20 : -20 }}
						animate={{ opacity: 1, x: 0 }}
						transition={{
							duration: 0.5,
							delay: 1,
						}}
						className={styles.formDiv}>
						<Flex align="center" justify="center" gap={20} style={{ marginBottom: 30 }}>
							<a href="https://hefth.sa/" className="btn btn-primary">
								{t('LOGIN.HOME')}
							</a>
						</Flex>

						<div className={`px-3`}>
							<img className="d-block d-md-none mb-5 m-auto" style={{ width: 200 }} src={darkLogo} alt="logo" />

							<div className="text-center mb-5">
								<h2>{t('LOGIN.TITLE')}</h2>
								<p className="text-light">{t('LOGIN.SUBTITLE')}</p>
							</div>

							<Form form={loginForm} name="loginForm" onFinish={onFinish}>
								<Form.Item
									name="phone_number"
									style={{ maxWidth: '100%' }}
									rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
									<PhoneInputComponent onChange={onChangePhone} fullWidth />
								</Form.Item>

								<Form.Item name="phone_country_code" hidden>
									<Input />
								</Form.Item>

								<FormPassword
									name="password"
									label={t('COMMON.PASSWORD')}
									rules={[{ required: true, message: t('COMMON.PASSWORD_REQUIRED') }]}
									style={{ maxWidth: 'unset' }}
								/>

								<Flex justify="space-between" align="center" className="my-4">
									<Link to="/reset-password" style={{ textDecoration: 'none' }}>
										{t('LOGIN.FORGET_PASSWORD')}
									</Link>

									<Checkbox onChange={(e) => setRemember(e.target.checked)}>{t('LOGIN.REMEMBER_ME')}</Checkbox>
								</Flex>

								<Form.Item style={{ maxWidth: 'unset' }}>
									<Button block type="primary" htmlType="submit" loading={isLoading}>
										{t('LOGIN.LOGIN')}
									</Button>
								</Form.Item>
							</Form>

							<p className="text-center mt-4">
								{t('LOGIN.DONT_HAVE_ACCOUNT')}{' '}
								<Button type="link" className="p-0" onClick={() => setRegisterModalOpen(true)}>
									{t('LOGIN.REGISTER_NOW')}
								</Button>
							</p>
						</div>
					</motion.div>
				</Flex>
			</Flex>

			<footer className={styles.loginFooter}>
				<Animate delay={1}>
					{t('COMMON.POWERED_BY')} <span className="fw-bold">{t('COMMON.TAKHAROJ_FINANCE')}</span> {new Date().getFullYear()}{' '}
					{t('COMMON.HEFTH_PLATFORM')}.
				</Animate>
			</footer>

			<ConfirmModalComponent
				large
				open={registerModalOpen}
				confirmText={t('LOGIN.REGISTER_NOW')}
				onConfirm={() => {
					navigate('/register');
				}}
				onDiscard={() => setRegisterModalOpen(false)}
				title={t('LOGIN.REGISTER_COMPANY_ACCOUNT')}
				note={
					<>
						<p className="mb-1">{t('LOGIN.REGISTER_HEADER')}</p>
						<ul style={{ listStyle: '', paddingInlineStart: '1rem' }}>
							<li>{t('LOGIN.REGISTER_POINT_1')}</li>
							<li>{t('LOGIN.REGISTER_POINT_2')}</li>
							<li>{t('LOGIN.REGISTER_POINT_3')}</li>
						</ul>
					</>
				}
				children={
					(tanaqulAppsUrl?.apple_store_link || tanaqulAppsUrl?.google_play_link) && (
						<Flex vertical gap={10} justify="center" align="center" className="my-3">
							<p className="mb-0">{t('LOGIN.SHAREHOLDER_LOGIN')}</p>

							<Flex gap={10} justify="center" align="center">
								{tanaqulAppsUrl?.apple_store_link && (
									<a href={tanaqulAppsUrl?.apple_store_link} target="_blank">
										<img style={{ width: 120, height: 40 }} src={appStoreIcon} alt="app_store" />
									</a>
								)}

								{tanaqulAppsUrl?.google_play_link && (
									<a href={tanaqulAppsUrl?.google_play_link} target="_blank">
										<img style={{ width: 120, height: 40 }} src={playStoreIcon} alt="play_store" />
									</a>
								)}
							</Flex>
						</Flex>
					)
				}
			/>
		</>
	);
}

export default LoginPage;
